import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { SvgAsset } from '@ingenico/react-ui/svg-asset';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularPercentageInfo } from '../circular-percentage-info';
import type { LogicalDeviceSummaryHardware } from '../logical-device-summary-card/logicalDeviceSummaryCard.model';
import styles from './logicalDeviceHardwareStatusCharts.module.scss';
import {
  checkShouldDisplayChart,
  getDisplayedPercentageValue,
  getEmptyClassName,
} from './logicalDeviceHardwareStatusCharts.util';

export interface LogicalDeviceHardwareStatusChartsProps {
  readonly hardware?: Partial<LogicalDeviceSummaryHardware>;
  readonly componentsClassNames?: Partial<Record<'container' | 'itemContainer', string>>;
  readonly displayEmptyAsDash?: boolean;
}

export function LogicalDeviceHardwareStatusCharts({
  hardware,
  componentsClassNames,
  displayEmptyAsDash,
}: LogicalDeviceHardwareStatusChartsProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  return (
    <div className={classNames(styles.container, componentsClassNames?.container)}>
      {checkShouldDisplayChart(hardware, 'battery', displayEmptyAsDash) ? (
        <CircularPercentageInfo
          componentsClassNames={{
            container: classNames(
              styles.chartContainer,
              componentsClassNames?.itemContainer,
              getEmptyClassName(hardware, 'battery'),
            ),
            childContainer: styles.childContainer,
          }}
          subtitle={getDisplayedPercentageValue({
            t,
            displayEmptyAsDash,
            value: hardware?.battery?.value,
          })}
          title={t('hardwareStatus.battery')}
          value={hardware?.battery?.value ?? 0}
        >
          <SvgAsset image="bolt" />
        </CircularPercentageInfo>
      ) : undefined}

      {checkShouldDisplayChart(hardware, 'flash', displayEmptyAsDash) ? (
        <CircularPercentageInfo
          componentsClassNames={{
            container: classNames(
              styles.chartContainer,
              componentsClassNames?.itemContainer,
              getEmptyClassName(hardware, 'flash'),
            ),
            childContainer: styles.childContainer,
          }}
          inverse={hardware?.flash?.value !== undefined}
          subtitle={getDisplayedPercentageValue({
            t,
            addFreeSuffix: true,
            displayEmptyAsDash,
            inverse: true,
            value: hardware?.flash?.value,
          })}
          title={t('hardwareStatus.flash')}
          value={hardware?.flash?.value ?? 0}
        >
          <SvgAsset image="speed" />
        </CircularPercentageInfo>
      ) : undefined}
      {checkShouldDisplayChart(hardware, 'RAM', displayEmptyAsDash) ? (
        <CircularPercentageInfo
          componentsClassNames={{
            container: classNames(
              styles.chartContainer,
              componentsClassNames?.itemContainer,
              getEmptyClassName(hardware, 'RAM'),
            ),
            childContainer: styles.childContainer,
          }}
          inverse={hardware?.RAM?.value !== undefined}
          subtitle={getDisplayedPercentageValue({
            t,
            addFreeSuffix: true,
            displayEmptyAsDash,
            inverse: true,
            value: hardware?.RAM?.value,
          })}
          title={t('hardwareStatus.ram')}
          value={hardware?.RAM?.value ?? 0}
        >
          <SvgAsset image="memory" />
        </CircularPercentageInfo>
      ) : undefined}
      {checkShouldDisplayChart(hardware, 'CPU', displayEmptyAsDash) ? (
        <CircularPercentageInfo
          componentsClassNames={{
            container: classNames(
              styles.chartContainer,
              componentsClassNames?.itemContainer,
              getEmptyClassName(hardware, 'CPU'),
            ),
            childContainer: styles.childContainer,
          }}
          inverse={hardware?.CPU?.value !== undefined}
          subtitle={getDisplayedPercentageValue({
            t,
            addFreeSuffix: true,
            displayEmptyAsDash,
            inverse: true,
            value: hardware?.CPU?.value,
          })}
          title={t('hardwareStatus.cpu')}
          value={hardware?.CPU?.value ?? 0}
        >
          <SvgAsset image="memory" />
        </CircularPercentageInfo>
      ) : undefined}
    </div>
  );
}
